import { toBigWithDefault, Unit, useCallbackRef, type NumberInputProps, type NumericField } from '@talos/kyoko';
import { PricingNumberInput } from './PricingNumberInput';

type PricingPxNumberInputProps = {
  field: NumericField;
  type: 'bid' | 'ask';
} & Omit<NumberInputProps, 'value' | 'disabled' | 'invalid' | 'prefix' | 'variant'>;

export const PricingPxNumberInput = ({ field, type, ...numberInputProps }: PricingPxNumberInputProps) => {
  const showIncrementDecrementButtons = field.unit === Unit.Bps;

  const handleIncrement = useCallbackRef(() => {
    // Bps price inputs step by one displayed bps.
    numberInputProps.onChange(toBigWithDefault(field.displayValue, 0).add(1).toFixed());
  });

  const handleDecrement = useCallbackRef(() => {
    // Bps price inputs step by one displayed bps.
    numberInputProps.onChange(toBigWithDefault(field.displayValue, 0).sub(1).toFixed());
  });

  // When pricing in bps (relative pricing), a positive bid or a negative ask means that you are _paying_ the counterparty.
  const isPayingForFlow =
    field.unit !== Unit.Bps ? false : type === 'bid' ? field.bigValue?.gt(0) : field.bigValue?.lt(0);
  const tooltip = field.errorString ? field.errorString : isPayingForFlow ? 'Paying for flow' : undefined;
  const iconVariant = field.errorString ? 'colorTextNegative' : isPayingForFlow ? 'colorTextWarning' : undefined;

  return (
    <PricingNumberInput
      field={field}
      showIncrementDecrementButtons={showIncrementDecrementButtons}
      onIncrement={handleIncrement}
      onDecrement={handleDecrement}
      tooltip={tooltip}
      iconVariant={iconVariant}
      {...numberInputProps}
    />
  );
};
